import React from 'react';
import { COLORS, FONTS, GradientBanner } from '../signup/signupStyles';

const TYPE_COLORS = {
  Interview: '#5B8FB9', Survey: '#6b8e23',
  'Focus Group': '#8B6DB0', 'Advisory Board': '#C27B3E'
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '24px' }}>
    <h3 style={{ fontSize: '14px', fontWeight: '700', color: COLORS.text, margin: '0 0 10px' }}>
      {title}
    </h3>
    {children}
  </div>
);

const Step = ({ number, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
    <div style={{
      width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
      background: `linear-gradient(135deg, ${COLORS.salmon} 0%, ${COLORS.peach} 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '14px', fontWeight: '700', color: '#fff'
    }}>{number}</div>
    <span style={{ fontSize: '14px', color: COLORS.text }}>{label}</span>
  </div>
);

const FaqItem = ({ question, answer }) => (
  <div style={{ marginBottom: '16px' }}>
    <p style={{ fontSize: '14px', fontWeight: '600', color: COLORS.text, margin: '0 0 4px' }}>
      {question}
    </p>
    <p style={{ fontSize: '14px', color: COLORS.muted, margin: 0, lineHeight: '1.5' }}>
      {answer}
    </p>
  </div>
);

const CompensationCard = ({ opp }) => (
  <div style={{
    backgroundColor: '#fff', borderRadius: '16px', padding: '28px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: '80px'
  }}>
    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
      <div style={{ fontSize: '32px', fontWeight: '700', color: '#6b8e23' }}>{opp.pay}</div>
      <div style={{ fontSize: '13px', color: COLORS.muted }}>Total Compensation</div>
    </div>
    <a href={opp.url} target="_blank" rel="noopener noreferrer" style={{
      display: 'block', textAlign: 'center', padding: '14px',
      fontSize: '16px', fontWeight: '600', color: '#fff',
      background: `linear-gradient(135deg, ${COLORS.salmon} 0%, ${COLORS.peach} 50%, ${COLORS.gold} 100%)`,
      borderRadius: '8px', textDecoration: 'none', marginBottom: '24px',
      transition: 'transform 0.2s, box-shadow 0.2s'
    }}
      onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 4px 12px rgba(235, 160, 155, 0.4)'; }}
      onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = 'none'; }}
    >Apply</a>
    {opp.referralPay && (
      <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '22px', fontWeight: '700', color: COLORS.text }}>{opp.referralPay}</div>
        <div style={{ fontSize: '13px', color: COLORS.muted, marginBottom: '8px' }}>Per Referral</div>
        <p style={{ fontSize: '12px', color: COLORS.muted, margin: 0, lineHeight: '1.5' }}>
          Earn {opp.referralPay} for each person you refer who completes this study.
        </p>
      </div>
    )}
  </div>
);

export const OpportunityDetail = ({ opportunity, onBack }) => {
  const typeColor = TYPE_COLORS[opportunity.type] || COLORS.blue;

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: COLORS.bg, padding: '80px 20px 60px',
      fontFamily: FONTS.system, textAlign: 'left'
    }}>
      <GradientBanner />
      <style>{`
        @media (max-width: 768px) {
          .opp-detail-layout { flex-direction: column !important; }
          .opp-detail-sidebar { position: static !important; width: 100% !important; }
        }
      `}</style>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <button onClick={onBack} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          fontSize: '14px', color: COLORS.muted, background: 'none',
          border: 'none', cursor: 'pointer', padding: '0', marginBottom: '24px'
        }}>
          <span style={{ fontSize: '18px' }}>&lsaquo;</span> Back to opportunities
        </button>

        <div className="opp-detail-layout" style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            {/* Main info card */}
            <div style={{
              backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
              boxShadow: '0 2px 16px rgba(0,0,0,0.06)', marginBottom: '24px'
            }}>
              <span style={{
                display: 'inline-block', fontSize: '11px', fontWeight: '600',
                textTransform: 'uppercase', letterSpacing: '0.5px',
                color: typeColor, backgroundColor: `${typeColor}12`,
                padding: '4px 10px', borderRadius: '4px', marginBottom: '16px'
              }}>{opportunity.type}</span>

              <h1 style={{
                fontFamily: FONTS.serif, fontSize: 'clamp(22px, 4vw, 28px)',
                fontWeight: '700', color: COLORS.text, margin: '0 0 12px'
              }}>{opportunity.title}</h1>

              <p style={{ fontSize: '16px', color: COLORS.muted, lineHeight: '1.6', margin: '0 0 28px' }}>
                {opportunity.description}
              </p>

              <Section title="Details">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {opportunity.details.map((d, i) => (
                    <li key={i} style={{ fontSize: '15px', color: COLORS.text, lineHeight: '1.8' }}>{d}</li>
                  ))}
                </ul>
              </Section>

              <Section title="Purpose">
                <p style={{ fontSize: '15px', color: COLORS.text, lineHeight: '1.6', margin: 0 }}>
                  {opportunity.purpose}
                </p>
              </Section>

              <Section title="Requirements">
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {opportunity.requirements.map((r, i) => (
                    <li key={i} style={{ fontSize: '15px', color: COLORS.text, lineHeight: '1.8' }}>{r}</li>
                  ))}
                </ul>
              </Section>
            </div>

            {/* What to expect */}
            <div style={{
              backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
              boxShadow: '0 2px 16px rgba(0,0,0,0.06)', marginBottom: '24px'
            }}>
              <h2 style={{
                fontFamily: FONTS.serif, fontSize: '20px', fontWeight: '700',
                color: COLORS.text, margin: '0 0 20px'
              }}>What to expect</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Step number={1} label="Apply and answer a few screening questions" />
                <Step number={2} label="Our team reviews your profile within 2-3 business days" />
                <Step number={3} label="If selected, schedule at a time that works for you" />
                <Step number={4} label={`Complete the ${opportunity.type.toLowerCase()} and get paid`} />
              </div>
            </div>

            {/* FAQ */}
            <div style={{
              backgroundColor: '#fff', borderRadius: '16px', padding: '32px',
              boxShadow: '0 2px 16px rgba(0,0,0,0.06)'
            }}>
              <h2 style={{
                fontFamily: FONTS.serif, fontSize: '20px', fontWeight: '700',
                color: COLORS.text, margin: '0 0 20px'
              }}>Common questions</h2>
              <FaqItem
                question="How long until I hear back?"
                answer="Most applicants hear back within 2-3 business days. If selected, we'll reach out to schedule."
              />
              <FaqItem
                question="Is my information kept confidential?"
                answer="Yes. Researchers never see your personal contact information. All responses are anonymized before sharing."
              />
              <FaqItem
                question="How do I get paid?"
                answer="Compensation is sent via your preferred method (Venmo, PayPal, or check) within 5 business days of completing the study."
              />
              <FaqItem
                question="Can I participate in multiple studies?"
                answer="Absolutely. Many of our members participate in several studies per year. Each one has its own eligibility criteria."
              />
            </div>
          </div>

          {/* Sidebar */}
          <div className="opp-detail-sidebar" style={{ width: '280px', flexShrink: 0 }}>
            <CompensationCard opp={opportunity} />
          </div>
        </div>
      </div>
    </div>
  );
};
